import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useData } from "../data/DataProvider";
import { roleColor, ROLE_COLORS } from "../data/roleColors";
import { isOpenSeat } from "../services/roles";
import "../styles.css";

function getInitials(name) {
  return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
}

function statusColor(s) {
  return s === "Active" ? "var(--green)" : s === "Potential" ? "var(--gold-text)" : s === "Completed" ? "var(--text-muted)" : "var(--blue)";
}

function ProjectCard({ project, teamMembers, people }) {
  const members = teamMembers.filter(m => m.ProjectJobNumber === project.Title);
  // Open seats are unfilled roles — they aren't people, so they don't count
  // toward the profiled % and render as a distinct dashed placeholder.
  const filled = members.filter(m => !isOpenSeat(m));
  const openSeats = members.filter(isOpenSeat);
  const profiledCount = filled.filter(m => people.find(p => p.FullName === m.PersonName)).length;
  const pct = filled.length > 0 ? Math.round((profiledCount / filled.length) * 100) : 0;
  const allDone = profiledCount === filled.length && filled.length > 0;
  const dueDate = project.CompletionDate
    ? new Date(project.CompletionDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const shown = filled.slice(0, 6);
  const extra = filled.length - shown.length;

  return (
    <Link to={`/projects/${encodeURIComponent(project.Title)}`} className={`project-card ${allDone ? "done" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="project-job-number">{project.Title}</div>
        <span style={{ fontSize: "10px", fontWeight: 800, color: statusColor(project.Status), textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {project.Status || "Active"}
        </span>
      </div>
      <div className="project-name">{project.JobName}</div>
      <div className="project-address">{project.Address || " "}</div>

      <div className="project-meta">
        {project.ProjectExecutive && <span className="meta-tag px">PX: {project.ProjectExecutive}</span>}
        {project.ProjectAccountant && <span className="meta-tag acct">{project.ProjectAccountant}</span>}
        {dueDate && <span className="meta-tag due">Due {dueDate}</span>}
      </div>

      <div className="divider-line" />

      <div className="flex items-center justify-between">
        <div className="team-avatars">
          {shown.map((m, i) => (
            <div key={i} className="avatar" style={{ background: roleColor(m.ProjectRole) }} title={`${m.PersonName} · ${m.ProjectRole}`}>
              {getInitials(m.PersonName)}
            </div>
          ))}
          {extra > 0 && <div className="avatar other">+{extra}</div>}
          {openSeats.length > 0 && (
            <div className="avatar open-seat" title={`${openSeats.length} open seat${openSeats.length === 1 ? "" : "s"}: ${openSeats.map(s => s.ProjectRole).join(", ")}`}>
              +{openSeats.length}
            </div>
          )}
          {filled.length === 0 && openSeats.length === 0 && (
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>No one assigned yet</span>
          )}
        </div>
      </div>

      <div className="divider-line" />

      <div className="progress-row">
        <div className="progress-track">
          <div className={`progress-fill ${allDone ? "done" : profiledCount === 0 ? "low" : ""}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="progress-label">
          {profiledCount}/{filled.length} profiled{openSeats.length > 0 ? ` · ${openSeats.length} open` : ""}
        </div>
      </div>
    </Link>
  );
}

const STATUSES = ["Active", "Potential", "Completed", "All"];

export default function Dashboard() {
  const { projects, teamMembers, people, loading, error } = useData();
  const [statusFilter, setStatusFilter] = useState("Active");
  const [pxFilter, setPxFilter] = useState("All");

  if (loading) {
    return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading projects from SharePoint…</div></div></div>;
  }
  if (error) {
    return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;
  }

  const activeCount = projects.filter(p => (p.Status || "Active") === "Active").length;
  const potentialCount = projects.filter(p => p.Status === "Potential").length;
  // Distinct staffed people — open seats are unfilled roles, not people.
  const staffedNames = [...new Set(teamMembers.filter(m => !isOpenSeat(m)).map(m => m.PersonName))];
  const totalPeople = staffedNames.length;
  const profiledNames = new Set(people.filter(p => p.IsActive).map(p => p.FullName));
  const profiledPeople = staffedNames.filter(n => profiledNames.has(n)).length;

  const statusProjects = statusFilter === "All" ? projects : projects.filter(p => (p.Status || "Active") === statusFilter);
  const pxList = ["All", ...[...new Set(statusProjects.map(p => p.ProjectExecutive).filter(Boolean))].sort((a, b) => a.localeCompare(b))];
  const visibleProjects = pxFilter === "All" ? statusProjects : statusProjects.filter(p => p.ProjectExecutive === pxFilter);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Projects</div>
          <div className="page-subtitle">Project teams &amp; pipeline</div>
        </div>
        <div className="stat-row">
          <div className="stat-card">
            <div className="stat-bar" style={{ background: "var(--green-bar)" }} />
            <div className="stat-num">{activeCount}</div>
            <div className="stat-label">Active Jobs</div>
          </div>
          <div className="stat-card">
            <div className="stat-bar" style={{ background: "var(--gold)" }} />
            <div className="stat-num" style={{ color: "var(--gold-text)" }}>{potentialCount}</div>
            <div className="stat-label">Potential</div>
          </div>
          <div className="stat-card">
            <div className="stat-bar" style={{ background: "var(--navy-mid)" }} />
            <div className="stat-num">{totalPeople}</div>
            <div className="stat-label">Team Members</div>
          </div>
          <div className="stat-card">
            <div className="stat-bar" style={{ background: "var(--green-bar)" }} />
            <div className="stat-num">{profiledPeople} <span className="sub">/ {totalPeople}</span></div>
            <div className="stat-label">CI Profiles</div>
          </div>
        </div>
      </div>

      <div className="section-header">
        <div className="filter-row">
          {STATUSES.map(s => (
            <div key={s} className={`filter-chip ${statusFilter === s ? "active" : ""}`} onClick={() => { setStatusFilter(s); setPxFilter("All"); }}>{s}</div>
          ))}
        </div>
        <div className="filter-row">
          {pxList.map(px => (
            <div key={px} className={`filter-chip ${pxFilter === px ? "active" : ""}`} onClick={() => setPxFilter(px)}>
              {px === "All" ? "All PX" : px}
            </div>
          ))}
        </div>
      </div>

      <div className="legend-row">
        {ROLE_COLORS.map(rc => (
          <span key={rc.label} className="legend-item">
            <span className="legend-swatch" style={{ background: rc.color }} /> {rc.label}
          </span>
        ))}
      </div>

      {visibleProjects.length === 0 ? (
        <div className="empty"><div className="empty-text">No {statusFilter !== "All" ? statusFilter.toLowerCase() : ""} projects to show.</div></div>
      ) : (
        <div className="project-grid">
          {visibleProjects.map(project => (
            <ProjectCard key={project.Id} project={project} teamMembers={teamMembers} people={people} />
          ))}
        </div>
      )}
    </div>
  );
}